import type { SaveData } from "./Save";
import type { AchievementEvent } from "./achievementDefs";

export type QuestReward = { xp: number; skillPoints?: number };

export interface QuestDef {
  id: string;
  level: number;
  name: string;
  desc: string;
  target?: number;
  /** Existing achievement that completes this quest, including retroactively. */
  achievementId?: string;
  reward: QuestReward;
  check: (event: AchievementEvent | null, save: SaveData) => number | true | null;
}

const achievement = (id: string) => (_event: AchievementEvent | null, save: SaveData) =>
  save.achievements.unlocked[id] ? true : null;

export const QUEST_DEFS: QuestDef[] = [
  {
    id: "quest.oath.first-victory", level: 1,
    name: "The First Oath", desc: "Win your first campaign run.",
    achievementId: "camp.first.win", reward: { xp: 150 }, check: achievement("camp.first.win"),
  },
  {
    id: "quest.oath.archer-corps", level: 1,
    name: "Raise the Archer Corps", desc: "Build 3 Archer towers across all runs.",
    target: 3, reward: { xp: 200 },
    check: (_event, save) => save.codex.towersBuilt.archer ?? 0,
  },
  {
    id: "quest.oath.goblin-patrol", level: 1,
    name: "Goblin Patrol", desc: "Defeat 25 Goblins across all runs.",
    target: 25, reward: { xp: 250 },
    check: (_event, save) => save.codex.enemiesKilled.goblin ?? 0,
  },
  {
    id: "quest.oath.untouched", level: 1,
    name: "Keep the Walls Untouched", desc: "Win a campaign map without a leak.",
    achievementId: "camp.flawless.any", reward: { xp: 500, skillPoints: 1 }, check: achievement("camp.flawless.any"),
  },
  {
    id: "quest.vanguard.hundred-tides", level: 2,
    name: "Hundred Tides", desc: "Clear 100 waves across all runs.",
    // Counts the stat directly rather than the matching achievement: the
    // unlock condition is identical, but this reports partial progress so the
    // board's bar fills instead of jumping 0 → done.
    target: 100, achievementId: "meta.waves.100", reward: { xp: 750 },
    check: (_event, save) => save.stats.wavesCleared,
  },
  {
    id: "quest.vanguard.knight-errant", level: 2,
    name: "Knight Errant", desc: "Win every campaign map on Knight difficulty.",
    achievementId: "camp.all.knight", reward: { xp: 1200, skillPoints: 1 }, check: achievement("camp.all.knight"),
  },
  {
    // Id kept from its original chapter-2 grouping so existing saves keep the
    // completion (and don't get re-awarded XP on reconcile).
    id: "quest.vanguard.bane", level: 3,
    name: "Bane of the Mighty", desc: "Defeat every campaign boss at least once.",
    achievementId: "slay.boss.all", reward: { xp: 1400, skillPoints: 1 }, check: achievement("slay.boss.all"),
  },
  {
    id: "quest.reckoning.diversified", level: 3,
    name: "Master of the Field", desc: "Win any map using at least 5 different tower types.",
    achievementId: "chal.diversified", reward: { xp: 900 }, check: achievement("chal.diversified"),
  },
  {
    id: "quest.crown.endless-vigil", level: 4,
    name: "Endless Vigil", desc: "Clear 500 waves across all runs.",
    target: 500, achievementId: "meta.waves.500", reward: { xp: 1600, skillPoints: 1 },
    check: (_event, save) => save.stats.wavesCleared,
  },
  {
    id: "quest.crown.of-the-realm", level: 4,
    name: "Crown of the Realm", desc: "Win every campaign map on Lord difficulty.",
    achievementId: "camp.all.lord", reward: { xp: 2500, skillPoints: 2 }, check: achievement("camp.all.lord"),
  },
];

export interface QuestLevel {
  id: number;
  name: string;
  subtitle: string;
  /** Texture key drawn inside the level's flag on the journey map. */
  icon: string;
}

export const QUEST_LEVELS: QuestLevel[] = [
  { id: 1, name: "The Defender's Oath", subtitle: "Secure the realm and prove your first command.", icon: "icon-quest-tower" },
  { id: 2, name: "The Vanguard", subtitle: "Master the campaign and face its greatest threats.", icon: "icon-swords" },
  { id: 3, name: "The Reckoning", subtitle: "Break the champions that hold the realm's borders.", icon: "icon-skull" },
  { id: 4, name: "The Crown", subtitle: "Claim dominion over every road and every siege.", icon: "icon-trophy" },
];

export function isQuestLevelComplete(level: number, save: SaveData): boolean {
  const defs = QUEST_DEFS.filter(q => q.level === level);
  return defs.length > 0 && defs.every(q => !!save.quests.completed[q.id]);
}

/** A level opens once every quest in the level before it is completed. */
export function isQuestLevelUnlocked(level: number, save: SaveData): boolean {
  return level <= 1 || isQuestLevelComplete(level - 1, save);
}

/** Target-quest progress, adjusted for stats accrued before the quest's level
 * unlocked (see `save.quests.baselines`). Clamped to the quest's target. */
export function questProgress(quest: QuestDef, save: SaveData): number {
  if (quest.target == null) return 0;
  const raw = quest.check(null, save);
  if (typeof raw !== "number") return quest.target;
  const baseline = save.quests.baselines[quest.id] ?? 0;
  return Math.max(0, Math.min(quest.target, raw - baseline));
}
